/**
 * Leader election for the telephony owner-tab.
 *
 * Exactly one browser tab owns the TelnyxRTC connection + the WebRTC audio.
 * That "owner" is elected with the Web Locks API: we hold a single named lock
 * for the entire lifetime of the owner tab. When the owner closes or crashes,
 * the browser releases the lock automatically and the next tab waiting on it
 * acquires ownership — race-free, and without any heartbeat bookkeeping.
 *
 * Followers never connect; they mirror the owner's state over the phone-bus
 * and proxy commands to it (see `phone-bus.ts` / `phone-state.ts`).
 *
 * Fallback: browsers without the Web Locks API (legacy only — the office-app
 * targets modern desktop Chrome) fall back to a BroadcastChannel "smallest
 * live id wins" election with a heartbeat TTL. It self-heals when the owner
 * stops broadcasting.
 */

export interface LeadershipCallbacks {
  onBecomeLeader: () => void;
  onResign: () => void;
}

const LOCK_NAME = 'cinco-phone-leader';

let leaderState = false;
let started = false;
let teardownFn: (() => void) | null = null;

type LeadershipListener = (isLeader: boolean) => void;
const listeners = new Set<LeadershipListener>();

export function isLeader(): boolean {
  return leaderState;
}

export function subscribeLeadership(cb: LeadershipListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function setLeader(next: boolean): void {
  if (leaderState === next) return;
  leaderState = next;
  listeners.forEach((l) => {
    try {
      l(next);
    } catch (err) {
      console.error('[telephony/leader] listener error', err);
    }
  });
}

/**
 * Begin contending for leadership. Returns immediately; `onBecomeLeader` fires
 * (possibly later) once this tab owns the lock, `onResign` if it ever loses it.
 * The returned teardown relinquishes the lock + contention. Idempotent:
 * repeated calls return the same teardown.
 */
export function acquireLeadership(callbacks: LeadershipCallbacks): () => void {
  if (started && teardownFn) return teardownFn;
  started = true;

  if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks) {
    teardownFn = acquireViaWebLocks(callbacks);
  } else {
    teardownFn = acquireViaBroadcast(callbacks);
  }
  return teardownFn;
}

// ─── Web Locks strategy (primary) ──────────────────────────────────

function acquireViaWebLocks(callbacks: LeadershipCallbacks): () => void {
  const controller = new AbortController();
  // Resolver van de granted-lock-promise. Een AbortSignal annuleert alleen een
  // nog PENDING lock-request; een al verleende lock komt uitsluitend vrij door
  // de callback-promise te resolven. Zonder deze resolver hield een dispose de
  // lock voor de rest van de tab-levensduur vast → permanent zero-leader.
  let releaseLock: (() => void) | null = null;

  navigator.locks
    .request(LOCK_NAME, { signal: controller.signal }, () => {
      // We hold the lock now → we are the owner. Keep it until teardown
      // resolves the promise below (releases the lock to the next waiter).
      setLeader(true);
      try {
        callbacks.onBecomeLeader();
      } catch (err) {
        console.error('[telephony/leader] onBecomeLeader threw', err);
      }
      return new Promise<void>((resolve) => {
        releaseLock = resolve;
      });
    })
    .catch((err: unknown) => {
      // AbortError is expected on teardown; anything else is a real failure.
      if ((err as { name?: string })?.name !== 'AbortError') {
        console.error('[telephony/leader] lock request failed', err);
      }
    });

  return () => {
    const wasLeader = leaderState;
    if (releaseLock) {
      // Lock is verleend: resolven = vrijgeven.
      releaseLock();
      releaseLock = null;
    } else {
      // Nog pending: het request zelf annuleren.
      controller.abort();
    }
    started = false;
    teardownFn = null;
    if (wasLeader) {
      setLeader(false);
      try {
        callbacks.onResign();
      } catch (err) {
        console.error('[telephony/leader] onResign threw', err);
      }
    }
  };
}

// ─── BroadcastChannel fallback (legacy browsers only) ──────────────

function acquireViaBroadcast(callbacks: LeadershipCallbacks): () => void {
  const CHANNEL = 'cinco-phone-leader-fallback';
  const HEARTBEAT_MS = 1000;
  const TTL_MS = 3000;
  const selfId = `${Date.now()}-${crypto.randomUUID()}`;

  let channel: BroadcastChannel | null = null;
  let settled = false; // delay the "I'm alone → I lead" decision one beat
  const peers = new Map<string, number>(); // id → lastSeen (epoch ms)

  function promote(): void {
    if (leaderState) return;
    setLeader(true);
    try {
      callbacks.onBecomeLeader();
    } catch (err) {
      console.error('[telephony/leader] onBecomeLeader threw', err);
    }
  }

  function demote(): void {
    if (!leaderState) return;
    setLeader(false);
    try {
      callbacks.onResign();
    } catch (err) {
      console.error('[telephony/leader] onResign threw', err);
    }
  }

  function recompute(): void {
    const now = Date.now();
    for (const [id, seen] of peers) {
      if (now - seen > TTL_MS) peers.delete(id);
    }
    let min = selfId;
    for (const id of peers.keys()) {
      if (id < min) min = id;
    }
    const shouldLead = min === selfId;
    if (shouldLead) {
      if (settled) promote();
    } else {
      demote();
    }
  }

  try {
    channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (event) => {
      const data = event.data as { type?: string; id?: string };
      if (!data?.id || data.id === selfId) return;
      if (data.type === 'alive') {
        peers.set(data.id, Date.now());
        recompute();
      } else if (data.type === 'bye') {
        peers.delete(data.id);
        recompute();
      }
    };
  } catch {
    // No BroadcastChannel either → we're the only tab; lead unconditionally.
    setLeader(true);
    try {
      callbacks.onBecomeLeader();
    } catch (err) {
      console.error('[telephony/leader] onBecomeLeader threw', err);
    }
    return () => {
      started = false;
      teardownFn = null;
      demote();
    };
  }

  const beat = () => {
    try {
      channel?.postMessage({ type: 'alive', id: selfId });
    } catch {
      /* ignore */
    }
    recompute();
  };
  beat();
  const settleTimer = setTimeout(() => {
    settled = true;
    recompute();
  }, HEARTBEAT_MS);
  const interval = setInterval(beat, HEARTBEAT_MS);

  return () => {
    clearTimeout(settleTimer);
    clearInterval(interval);
    try {
      channel?.postMessage({ type: 'bye', id: selfId });
    } catch {
      /* ignore */
    }
    try {
      channel?.close();
    } catch {
      /* ignore */
    }
    channel = null;
    started = false;
    teardownFn = null;
    demote();
  };
}
